"use client"

import { Accordion } from "@base-ui/react/accordion"
import { AnimatePresence, motion } from "framer-motion"
import Image from "next/image"
import type { ReactNode } from "react"
import { useState } from "react"
import AiIllustration from "./LandingPage/AiIllustration"
import EarnIllustration from "./LandingPage/EarnIllustration"
import ReceiveSendIllustration from "./LandingPage/ReceiveSendIllustration"
import RwaIllustration from "./LandingPage/RwaIllustration"
import SwapIllustration from "./LandingPage/SwapIllustration"
import TradingIllustration from "./LandingPage/TradingIllustration"

const features: Array<{
  title: string
  subtitle: string
  comingSoon?: boolean
  Illustration: (props: { isActive: boolean }) => ReactNode
}> = [
  {
    title: "Swap anything to anything",
    subtitle:
      "Buy ZEC with USDC? AAPL with BTC? Powered by NEAR Intents, swap nearly any asset across networks and asset classes—confidentially. Place your order and let market makers compete to offer you the best available price.",
    Illustration: SwapIllustration,
  },
  {
    title: "Send anything, anywhere",
    subtitle:
      "See USDC at checkout, but only have NEAR or ETH? Scan the QR code and choose what you want to spend. Universal Send handles the conversion and makes the payment—privately.",
    Illustration: ReceiveSendIllustration,
  },
  {
    title: "Invest in real-world assets",
    subtitle:
      "Why split your portfolio between a brokerage and a crypto wallet? Invest in equities, ETFs, bonds, precious metals, and crypto from one confidential account—with settlement in moments, not days.",
    Illustration: RwaIllustration,
  },
  {
    title: "Earn on your assets",
    subtitle:
      "Put your assets to work through professionally managed vaults and staking strategies. Your yield-earning USDC stays ready to move, trade, or spend just like regular USDC. That’s the magic of money built onchain.",
    Illustration: EarnIllustration,
  },
  {
    title: "Trade perpetual markets",
    subtitle:
      "Popularized by crypto, now moving beyond it. Take leveraged long or short positions across crypto and real-world markets, with no expiry dates.",
    Illustration: TradingIllustration,
  },
  {
    title: "Bank and spend from one account",
    comingSoon: true,
    subtitle:
      "Move between traditional currencies and digital assets, then spend from your balance with a debit card. Deposit, withdraw, and pay wherever cards are accepted.",
    Illustration: () => (
      <div className="flex size-full max-w-[min(85vw,24rem)] flex-col items-center justify-center">
        <div className="w-full pr-9 sm:mr-9 sm:pr-0">
          <Image
            src="/static/images/landing-card-dark.png"
            alt=""
            width={632}
            height={380}
            quality={90}
            className="pointer-events-none w-full select-none rounded-3xl object-contain shadow-lg"
          />
        </div>
        <div className="-mt-24 w-full pl-9 sm:ml-9 sm:pl-0">
          <Image
            src="/static/images/landing-card-light.png"
            alt=""
            width={632}
            height={380}
            quality={90}
            className="pointer-events-none select-none rounded-3xl object-contain shadow-lg"
          />
        </div>
      </div>
    ),
  },
  {
    title: "Understand your finances with AI",
    comingSoon: true,
    subtitle:
      "Get AI-assisted insights into your finances, including risks, opportunities, and what may need your attention. You stay in control and approve every action.",
    Illustration: AiIllustration,
  },
]

const illustrationVariants = {
  enter: (direction: number) => ({
    y: direction >= 0 ? 48 : -48,
    scale: 0.9,
    opacity: 0,
    filter: "blur(4px)",
  }),
  center: {
    y: 0,
    scale: 1,
    opacity: 1,
    filter: "blur(0px)",
  },
  exit: (direction: number) => ({
    y: direction >= 0 ? -48 : 48,
    scale: 0.9,
    opacity: 0,
    filter: "blur(4px)",
  }),
}

const Features = () => {
  const [openValue, setOpenValue] = useState([features[0].title])
  const [direction, setDirection] = useState(0)

  const activeFeatureIndex = Math.max(
    0,
    features.findIndex(({ title }) => title === openValue[0])
  )
  const activeFeature = features[activeFeatureIndex]
  const Illustration = activeFeature.Illustration

  return (
    <section className="mx-auto w-full max-w-7xl border-black/11 border-x pt-24 max-xl:border-none sm:pt-32">
      <header className="flex flex-col gap-4 px-6 pb-16 sm:px-8">
        <h2 className="text-balance font-medium text-3xl/9 text-gray-900 tracking-tight lg:text-[2.5rem]/10">
          Your money always wanted to do more. Now it can.
        </h2>
        <p className="text-balance font-medium text-gray-500 text-lg/7">
          Move it, trade it, invest it, and earn from it—without barriers.
        </p>
      </header>

      <div className="lg:grid lg:grid-cols-2 lg:items-stretch">
        <Accordion.Root
          multiple={false}
          value={openValue}
          onValueChange={(value, eventDetails) => {
            // Keep one panel open at all times.
            if (value.length === 0) {
              eventDetails.cancel()
              return
            }

            const nextIndex = features.findIndex(
              ({ title }) => title === value[0]
            )
            setDirection(nextIndex > activeFeatureIndex ? 1 : -1)
            setOpenValue(value)
          }}
          className="divide-y divide-black/11 border-black/11 border-y"
        >
          {features.map(({ title, subtitle, comingSoon }) => (
            <Accordion.Item
              key={title}
              value={title}
              className="transition-colors duration-150 data-open:bg-gray-50"
            >
              <Accordion.Header>
                <Accordion.Trigger className="flex w-full flex-wrap items-baseline gap-x-4 gap-y-2 px-6 py-6 text-left transition-[padding] duration-150 ease-[ease-out] focus-visible:outline-none data-panel-open:pb-3 sm:px-8">
                  <span className="font-medium text-gray-900 text-xl/7 tracking-tight">
                    {title}
                  </span>
                  {comingSoon && (
                    <span className="whitespace-nowrap font-semibold text-green-700 text-sm/5">
                      Coming soon
                    </span>
                  )}
                </Accordion.Trigger>
              </Accordion.Header>
              <Accordion.Panel className="h-(--accordion-panel-height) overflow-hidden transition-[height] duration-150 ease-[ease-out] data-ending-style:h-0 data-starting-style:h-0">
                <p className="text-pretty px-6 pb-6 font-medium text-base/6 text-gray-500 sm:px-8">
                  {subtitle}
                </p>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion.Root>

        <div className="relative flex min-h-100 items-center justify-center bg-[linear-gradient(180deg,#65FBAA_0%,#31F49C_50%,#00EC97_100%)] py-6 lg:min-h-0">
          <AnimatePresence initial={false} custom={direction} mode="popLayout">
            <motion.div
              key={activeFeature.title}
              custom={direction}
              variants={illustrationVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ type: "spring", bounce: 0, duration: 0.4 }}
              className="flex w-full items-center justify-center"
            >
              <Illustration isActive />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="mask-[url('/static/images/near-star-tile.svg')] mask-repeat mask-size-[21px_21px] h-32 bg-black/9" />
    </section>
  )
}

export default Features
